import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDateTime, tzDisplay } from "../lib/timezone";
import { EVENT_LABEL } from "../lib/advisories";
import AdvisoryMap from "../components/AdvisoryMap";
import AdvisoryForm from "../components/AdvisoryForm";
import { StatusBadge, CategoryBadge, UnmappedBadge, AdvisoryActionDialog } from "../components/AdvisoryBits";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ArrowLeft, Pencil, MessageSquarePlus, ShieldCheck, CheckCircle2, RotateCcw, MapPin, User, Clock, History, Lock } from "lucide-react";

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <div className="mt-0.5 text-sm text-slate-800">{children}</div>
    </div>
  );
}

const fmtVal = (v, tz) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) return fmtDateTime(v, tz);
  return String(v);
};

export default function AdvisoryDetails() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const tz = user?.org_timezone;
  const [a, setA] = useState(null);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [action, setAction] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => api.get(`/advisories/${id}`).then((r) => { setA(r.data); setError(null); }).catch((e) => setError(e.response?.status || 500)), [id]);
  useEffect(() => {
    load();
    api.get("/advisories/config").then((r) => setConfig(r.data)).catch(() => setConfig({ map_enabled: false, categories: [] }));
    api.get("/divisions").then((r) => setDivisions(r.data)).catch(() => {});
  }, [load]);

  if (error) {
    return (
      <Card className="rounded-md border border-red-200 bg-red-50 p-8 text-center" data-testid="advisory-access-denied">
        <Lock className="mx-auto h-6 w-6 text-red-500" />
        <p className="mt-2 font-display font-bold text-red-800">You do not have permission to view this record</p>
        <Link to="/advisories" className="mt-3 inline-block text-sm text-blue-700">Back to Advisories</Link>
      </Card>
    );
  }
  if (!a) return <div className="h-64 animate-pulse rounded-md bg-slate-200/70" />;

  const resolved = a.effective_status === "resolved";
  const canMaintain = a.can_maintain;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="advisory-details-page">
      <button type="button" onClick={() => navigate("/advisories")} data-testid="advisory-back" className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> All advisories</button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={a.effective_status} />
            <CategoryBadge category={a.category} />
            {!a.has_location && <UnmappedBadge />}
            <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{a.visibility === "org" ? "Agency-wide" : `${a.division} only`}</span>
          </div>
          <h1 className="mt-2 font-display text-2xl font-black tracking-tight text-slate-900 sm:text-3xl" data-testid="advisory-detail-title">{a.title}</h1>
          <p className="mt-1 flex items-center gap-1 text-sm text-slate-600"><MapPin className="h-4 w-4 shrink-0" /> {a.location_description}</p>
        </div>
        {canMaintain && (
          <div className="flex flex-wrap gap-2" data-testid="advisory-actions">
            <Button variant="outline" size="sm" data-testid="advisory-edit-button" onClick={() => setEditing(true)} className="gap-1"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
            <Button variant="outline" size="sm" data-testid="advisory-update-button" onClick={() => setAction("update")} className="gap-1"><MessageSquarePlus className="h-3.5 w-3.5" /> Add Update</Button>
            {!resolved && <Button size="sm" data-testid="advisory-verify-button" onClick={() => setAction("verify")} className="gap-1 bg-blue-600 text-white hover:bg-blue-700"><ShieldCheck className="h-3.5 w-3.5" /> Confirm Still Active</Button>}
            {!resolved && <Button size="sm" data-testid="advisory-resolve-button" onClick={() => setAction("resolve")} className="gap-1 bg-slate-800 text-white hover:bg-slate-900"><CheckCircle2 className="h-3.5 w-3.5" /> Mark Resolved</Button>}
            {resolved && <Button size="sm" data-testid="advisory-reopen-button" onClick={() => setAction("reopen")} className="gap-1 bg-amber-600 text-white hover:bg-amber-700"><RotateCcw className="h-3.5 w-3.5" /> Reopen</Button>}
          </div>
        )}
      </div>

      {a.effective_status === "needs_verification" && (
        <div data-testid="needs-verification-banner" className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="font-bold">Review deadline passed {fmtDateTime(a.next_review_at, tz)}.</span> This condition is still shown as active until someone confirms it or marks it resolved.
        </div>
      )}
      {resolved && (
        <div data-testid="resolved-banner" className="rounded-md border border-slate-300 bg-slate-100 px-4 py-3 text-sm text-slate-800">
          <span className="font-bold">Resolved by {a.resolved_by_name} · {fmtDateTime(a.resolved_at, tz)}.</span> {a.resolution_note}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm space-y-5">
            <Field label="What is affected" testid="advisory-description"><p className="whitespace-pre-wrap">{a.description}</p></Field>
            {a.access_notes && <Field label="Access / detour notes" testid="advisory-access-notes"><p className="whitespace-pre-wrap">{a.access_notes}</p></Field>}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Reporting source" testid="advisory-source">{a.source || "—"}</Field>
              <Field label="Expected end (estimate only)" testid="advisory-expected-end">
                {a.expected_end_at ? fmtDateTime(a.expected_end_at, tz) : "—"}
                <p className="text-[11px] text-slate-400">An estimate — does not confirm the condition has cleared.</p>
              </Field>
              <Field label="Review deadline" testid="advisory-review-deadline"><span className={a.effective_status === "needs_verification" ? "font-bold text-amber-700" : ""}>{fmtDateTime(a.next_review_at, tz)}</span></Field>
              <Field label="Last verified" testid="advisory-last-verified">
                {a.last_verified_at ? <>{a.last_verified_by_name} · {fmtDateTime(a.last_verified_at, tz)}</> : <span className="font-semibold text-amber-700">Not yet reverified</span>}
              </Field>
              <Field label="Created" testid="advisory-created"><User className="mr-1 inline h-3.5 w-3.5 text-slate-400" />{a.created_by_name} · {fmtDateTime(a.created_at, tz)}</Field>
              <Field label="Last updated" testid="advisory-updated"><Clock className="mr-1 inline h-3.5 w-3.5 text-slate-400" />{fmtDateTime(a.updated_at, tz)}</Field>
            </div>
            <p className="text-[11px] text-slate-400">Times shown in {tzDisplay(tz)}.</p>
          </Card>

          <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><History className="h-4 w-4 text-slate-500" /> Update &amp; lifecycle history</h2>
            <ol className="mt-4 space-y-3" data-testid="advisory-history">
              {(a.events || []).map((e) => (
                <li key={e.id} data-testid={`advisory-event-${e.event_type}`} className="relative border-l-2 border-slate-200 pl-4">
                  <span className={`absolute -left-[7px] top-1.5 h-3 w-3 rounded-full ${e.event_type === "verified" ? "bg-blue-600" : e.event_type === "resolved" ? "bg-slate-500" : e.event_type === "reopened" ? "bg-amber-500" : "bg-slate-300"}`} />
                  <p className="text-sm font-semibold text-slate-900">{EVENT_LABEL[e.event_type] || e.event_type}<span className="font-normal text-slate-500"> · {e.actor_name} · {fmtDateTime(e.created_at, tz)}</span></p>
                  {e.summary && e.event_type !== "update" && <p className="text-xs text-slate-600">{e.summary}</p>}
                  {e.note && <p className="mt-1 whitespace-pre-wrap rounded bg-slate-50 px-2 py-1.5 text-sm text-slate-800">{e.note}</p>}
                  {e.changes && Object.keys(e.changes).length > 0 && (
                    <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
                      {Object.entries(e.changes).map(([k, v]) => (
                        <li key={k}><span className="font-semibold">{k.replace(/_/g, " ")}:</span> <span className="line-through text-slate-400">{fmtVal(v.from, tz)}</span> → {fmtVal(v.to, tz)}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ol>
          </Card>
        </div>

        <div className="space-y-4">
          <div className="h-72">
            {config ? (a.has_location ? <AdvisoryMap testid="advisory-detail-map" config={config} advisories={[a]} selectedId={a.id} focus={{ lat: a.lat, lng: a.lng }} className="h-72" /> : (
              <Card className="flex h-72 flex-col items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50 text-center" data-testid="advisory-detail-unmapped">
                <UnmappedBadge />
                <p className="mt-2 max-w-[220px] text-xs text-slate-500">No pin was placed. {canMaintain ? "Edit to add a map location." : ""}</p>
              </Card>
            )) : <div className="h-72 animate-pulse rounded-md bg-slate-200/70" />}
          </div>
          {a.has_location && <p className="text-[11px] text-slate-400">{a.lat.toFixed(5)}, {a.lng.toFixed(5)}</p>}
        </div>
      </div>

      {action && <AdvisoryActionDialog action={action} advisory={a} onClose={() => setAction(null)} onDone={(d) => setA(d)} />}
      <AdvisoryForm open={editing} onClose={() => setEditing(false)} onSaved={() => load()} config={config} divisions={divisions} advisory={a} />
    </div>
  );
}
